import React, { useState, useEffect, useRef } from "react";
import { FmUnderlineNav, FmRadioButton, FmReleaseChart, FmWarningBox, FmAllModals, FmBlackPopup, FmButton, FmPageLoader } from "../../Components";
import { useIsMobile } from "../../Hooks";
import { useTranslation } from "react-i18next";
import { FmGreenDescription, FmProductTitle } from "./Components";
import { AgriSolutionState, GeneralState, RootState } from "../../Redux";
import { ZERO, ONE, DEALER_LINK } from "../../Utils/Consts";
import { SavedItemsState } from "../../Redux/States";
import { useTheme } from "../../Hooks";
import { calculateFertigationRate, delay, getCurrentMonthName } from "./Utils";
import useStyles from "./PageReleaseChart.style";
import { getMonthFromNumber } from "../../Utils/TimeUtils";
import { shareableItemTypes } from "../../Utils/Enums";
import { SMALL_MESURMENT_UNIT, LOGGED_IN_ROUTES, BIG_MESURMENT_UNIT } from "../../Constants";
import { useHistory } from "react-router-dom";
import countriesAndRegions from "../../Translations/Languages/he/countriesAndRegions";
import { GenericFormula, FarmerPractice, ReleaseGraph, CarbonFootprintResponse } from "../../Models";
import { Api } from "../../Services";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { ShowWhen } from "../../Components/ShowWhen";
import { FmCarbonFootprintTable } from "../../Components/FmCarbonFootprintTable";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { convertToImperial } from "../../Constants/Calculations";
import Config from "../../Configs";
import { CFPTableActions } from "../../Redux/States/CFPTable";
import { getTierZeroValueByName } from "../../Utils";
import html2canvas from "html2canvas";
import { useAppSelector, useAppDispatch } from "../../Redux/Hooks/Hooks";
import { FarmerNPK } from "../../types";
import { agriSolution } from "../../Redux/States/AgriSolution/AgriSolutionSlice";

const radioButtonArray = ["Line", "Bar"];

const PageReleaseChart = () => {
  const [graphType, setGraphType] = useState<string>("Line");
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const history = useHistory();
  const savedItemId = useAppSelector(AgriSolutionState.Selectors.selectItemId);
  const sharedItemId = useAppSelector(GeneralState.Selectors.selectSharedItemId);
  const recommendedSolutionName = useAppSelector(AgriSolutionState.Selectors.selectRecommendedSolutionName);
  const customizedData = useAppSelector(AgriSolutionState.Selectors.selectCustomizedState);
  const isAgri = useAppSelector(GeneralState.Selectors.selectIsAgri);
  const cropState = useAppSelector(AgriSolutionState.Selectors.selectCropsState);
  const recommendedMaterialCode = useAppSelector(AgriSolutionState.Selectors.selectRecommendedMaterialCode);
  const soilState = useAppSelector(AgriSolutionState.Selectors.selectSoilState);
  const farmerPracticeGraphString = useAppSelector((state: RootState) => state.agriSolution.releaseGraphState.farmerPracticeGraphString);
  const recommendedSolution = useAppSelector(AgriSolutionState.Selectors.selectRecommendedSolution);
  const releaseChartRef = useRef<any>();
  const [pdfLodaing, setPdfLoading] = useState<boolean>(false);

  const releaseGraphData = useAppSelector(AgriSolutionState.Selectors.selectReleaseGraphState);
  const displayReleaseGraphType = useAppSelector(AgriSolutionState.Selectors.selectDisplayReleaseGraphType);
  const isUserNavigateToMainPage = useAppSelector(GeneralState.Selectors.selectUserNavigateToMainPage);
  const customizedSolution = customizedData.updatedBestSolutionFormula
    ? customizedData.updatedBestSolutionFormula
    : customizedData.bestSolutionFormula;
  const [showSaveItemModal, setShowSaveItemModal] = useState<boolean>(false);
  const [ShowBlackSnackbar, setShowBlackSnackbar] = useState<boolean>(false);
  const isUserSave = useAppSelector(AgriSolutionState.Selectors.selectIsUserSave);
  const farmStateData = useAppSelector(AgriSolutionState.Selectors.selectFarmState);
  const { activeMetricMethod } = useAppSelector(GeneralState.Selectors.selectMetricMethods);
  const valueSelected = activeMetricMethod === "1" ? ZERO : ONE;
  const user = useAppSelector(GeneralState.Selectors.selectUser);
  const tableData = useAppSelector((state: RootState) => state.cfpTable.tableData);

  const { cropDemand } = useAppSelector(AgriSolutionState.Selectors.selectFarmState);
  const shelfSolutionCurrentDayReleaseRemainValues: GenericFormula[] = useAppSelector(
    AgriSolutionState.Selectors.selectShelfSolutionCurrentDayReleaseRemainValues
  );
  const customizedCurrentDayReleaseRemainValues: GenericFormula[] = useAppSelector(
    AgriSolutionState.Selectors.selectCustomizedSolutionCurrentDayReleaseRemainValues
  );
  const { selectedCrop, expectedYield, plantingDate, finalHarvestDate, selectedLocationCountryCode } = useAppSelector(
    AgriSolutionState.Selectors.selectCropsState
  );
  const isGreenDescOpen = useAppSelector((state: RootState) => state.agriSolution.releaseGraphState.isGreenDescriptionOpen);

  const userSolutionNPK = useAppSelector((state) => state.agriSolution.customizedState.formulaNutrients);
  const customSolutionObject = useAppSelector((state) => {
    const { n, p, k } = state.agriSolution.farmState.farmerPractice;
    const percents = state.agriSolution.customizedState.formulaNutrients;

    if (percents == null || n == null || p == null || k == null) return null;

    const sortedNPK: FarmerNPK[] = [
      { key: "n", value: n },
      { key: "p", value: p },
      { key: "k", value: k },
    ];
    sortedNPK.sort((a, b) => b.value - a.value);

    const customSolutionWeight = sortedNPK[0].value / (percents[sortedNPK[0].key]! / 100);
    const restOfElements = sortedNPK.slice(1).map((e) => ({ ...e, value: customSolutionWeight * (percents[e.key]! / 100) }));
    const customSolutionNPK = [sortedNPK[0], ...restOfElements];

    return { customSolutionWeight, customSolutionNPK };
  });

  console.log({ userSolutionNPK, customSolutionObject });

  const generatePdf = async () => {
    if (!releaseChartRef) return;

    setPdfLoading(true);

    const title =
      displayReleaseGraphType === shareableItemTypes.agri_multicote_customize
        ? customizedData.updatedBestSolutionFormula || customizedData.bestSolutionFormula
        : recommendedSolutionName;

    const selected = selectedCrop || "1";
    const selectedCountry = selectedLocationCountryCode || "1";
    const currentRecipe = customizedData.recipe;
    const canvas = await html2canvas(releaseChartRef.current);
    const canvasBase64 = canvas.toDataURL();

    const data = {
      name: user?.name,
      location: countriesAndRegions.countries[selectedCountry],
      phoneNumber: "",
      date: new Date().toDateString(),
      time: new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "numeric",
        minute: "numeric",
      }),
      bestSolutionFormula: title,
      bestSuitFormulaCoatings:
        displayReleaseGraphType === shareableItemTypes.agri_multicote_customize
          ? customizedData.updatedCoatingPercentage?.n
            ? `(${customizedData.updatedCoatingPercentage.n}-${customizedData.updatedCoatingPercentage.p}-${customizedData.updatedCoatingPercentage.k}%)`
            : customizedData.bestSuitFormulaCoatings
          : "",
      formulaNutrients: {
        n: cropDemand[valueSelected].cropDemand.n,
        p: cropDemand[valueSelected].cropDemand.p,
        k: cropDemand[valueSelected].cropDemand.k,
      },
      applicationVolume: {
        mesurmentUnit: releaseGraphData.applicationVolume[valueSelected].mesurmentUnit,
        value: releaseGraphData.applicationVolume[valueSelected].value,
      },
      yield: {
        mesurmentUnit: cropState.expectedYield,
        value: (BIG_MESURMENT_UNIT as any)[activeMetricMethod],
      },
      crop: t(`crops.${selected}`),
      plantingDate: plantingDate,
      finalHarvestDate: finalHarvestDate,
      water: t(`agriSolution.waterRegime.sliderMarks.${soilState.selectedWater}`),
      soil: t(`agriSolution.soilType.sliderMarks.${soilState.selectedWater}`),
      recommendedSolution: {
        materialCode: recommendedMaterialCode,
      },
      customized: {
        coated: currentRecipe.coated,
        uncoated: currentRecipe.unCoated,
      },
      chartImageBase64: canvasBase64,
      tableData: cfpResponse ? tableData : null,
    };

    try {
      const response = await Api.PdfService.generatePdf(data);

      if (response.error) {
        throw new Error(response.error);
      }

      const link = document.createElement("a");
      link.href = response.data;
      link.download = Date.now().toString() + ".pdf";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("PDF RESPONSE", response);
    } catch (err) {
      console.error(err);
    }

    setPdfLoading(false);
  };

  useEffect(() => {
    (window as any).dataLayer.push({
      event: "ReleaseGraphStage",
      pageUrl: "https://multimatch.haifa-group.com/multi-match/agri-solution/releaseChart",
      pageTitle: "ReleaseGraphStage",
    });

    console.log("use effect farmer npk", customSolutionObject);
    if (customSolutionObject != null) {
      console.log("there is farmer npk");
      dispatch(
        agriSolution.actions.setCustomSolution({
          customSolutionNPK: customSolutionObject.customSolutionNPK,
          customSolutionWeight: customSolutionObject.customSolutionWeight,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (sharedItemId && sharedItemId !== 0) {
      dispatch(GeneralState.Actions.setUserNeedToViewSharableItem(false));
      dispatch(SavedItemsState.Thunks.getSavedItemById(sharedItemId ? sharedItemId : 0));
      dispatch(GeneralState.Actions.setSharedSavedItemId(0));
    }
  }, [isUserNavigateToMainPage]);

  const handleClickOnShare = async () => {
    if (savedItemId && savedItemId != 0) {
      const type =
        displayReleaseGraphType == shareableItemTypes.agri_multicote_customize
          ? shareableItemTypes.agri_multicote_customize
          : shareableItemTypes.agri_multicote_ready_made;
      dispatch(AgriSolutionState.Actions.setIsUserShare(true));
      await dispatch(AgriSolutionState.Thunks.share(savedItemId ? savedItemId : 0, type));
    }
  };

  const openMailBoxToShareItem = () => {
    if (savedItemId && savedItemId != 0) {
      const link = escape(releaseGraphData.shareLink);
      const title = displayReleaseGraphType == shareableItemTypes.agri_multicote_customize ? customizedSolution : recommendedSolutionName;
      const body = `${t("ShareEmail.body.please")}%20${t("ShareEmail.body.find")}%20${t("ShareEmail.body.attached")}%20%0D%0A${link}%20%20${t(
        "ShareEmail.body.recommendation"
      )}%0D%0A${t("ShareEmail.body.Thank")}%20${t("ShareEmail.body.you")}%20${t("ShareEmail.body.Haifa")}%20${t("ShareEmail.body.Group")}`;
      window.open("mailto:?subject=" + t("ShareEmail.title") + title + "&body=" + body);
    } else {
      window.alert(`${t("PageReleaseChart.shareUnsavedItem")}`);
    }
  };

  useEffect(() => {
    console.log("useEffect graph", releaseGraphData);

    if (releaseGraphData.isUserShare && releaseGraphData.shareLink) {
      openMailBoxToShareItem();
      dispatch(AgriSolutionState.Actions.setIsUserShare(false));
    }
  }, [releaseGraphData]);

  const warningDescription = (
    <>
      <span>
        {t("PageReleaseChart.warning.description")}
        <a className={classes.boldText} href={DEALER_LINK} target="_blank" rel="noreferrer noopener">
          {t("PageReleaseChart.warning.boldDescription")}
        </a>
      </span>
    </>
  );

  const handleGraphType = (e: any) => {
    setGraphType(e.target.value);
  };

  const getCurrentDayReleasePercentage = (): string => {
    const arr: number[] = [];
    const graphIsCustomized = displayReleaseGraphType == shareableItemTypes.agri_multicote_customize;
    const cropDemand: FarmerPractice =
      farmStateData && farmStateData.cropDemand && farmStateData.cropDemand.length > 0
        ? farmStateData.cropDemand[valueSelected].cropDemand
        : { n: 0, p: 0, k: 0 };
    const remainingValues: GenericFormula = graphIsCustomized
      ? customizedCurrentDayReleaseRemainValues
        ? customizedCurrentDayReleaseRemainValues[valueSelected]
        : { n: 0, p: 0, k: 0 }
      : shelfSolutionCurrentDayReleaseRemainValues
      ? shelfSolutionCurrentDayReleaseRemainValues[valueSelected]
      : { n: 0, p: 0, k: 0 };

    if (remainingValues) {
      ["n", "p", "k"].map((i) => {
        arr.push(((remainingValues as any)[i] * 100) / (cropDemand as any)[i]);
      });
    }

    return `${arr[0].toFixed(1)}, ${arr[1].toFixed(1)}, ${arr[2].toFixed(1)}%`;
  };

  const currentReleaseValues =
    displayReleaseGraphType == shareableItemTypes.agri_multicote_customize
      ? customizedCurrentDayReleaseRemainValues
        ? customizedCurrentDayReleaseRemainValues[valueSelected]
        : null
      : shelfSolutionCurrentDayReleaseRemainValues
      ? shelfSolutionCurrentDayReleaseRemainValues[valueSelected]
      : null;

  const measureUnit = useAppSelector((state: RootState) => state.general.activeMetricMethod);

  const dataTitle = {
    farmerPraceiceNPKTitle: farmStateData.farmerPractice
      ? `${getCurrentMonthName()}: ${t("PageReleaseChart.currentDayRemainingReleaseValues.postValue")} ${
          valueSelected == ZERO ? t("pageStep4.table.nutrientsUnit") : t("pageStep4.table.nutrientsUnitImperial")
        }: ${measureUnit === "1" ? farmStateData.farmerPractice.n : convertToImperial(farmStateData.farmerPractice.n!)}, ${
          measureUnit === "1" ? farmStateData.farmerPractice.p : convertToImperial(farmStateData.farmerPractice.p!)
        }, ${measureUnit === "1" ? farmStateData.farmerPractice.k : convertToImperial(farmStateData.farmerPractice.k!)}`
      : "",
    currentReleaseString: currentReleaseValues
      ? `${getCurrentMonthName()}: ${t("PageReleaseChart.currentDayRemainingReleaseValues.postValue")} ${
          valueSelected == ZERO ? t("pageStep4.table.nutrientsUnit") : t("pageStep4.table.nutrientsUnitImperial")
        }: ${currentReleaseValues.n}, ${currentReleaseValues.p}, ${currentReleaseValues.k}`
      : "",
    farmerPracticeCurrentReleasePercentageString: farmerPracticeGraphString
      ? `${t("PageReleaseChart.currentDayRemainingReleaseValues.currentPrecentage")} ${farmerPracticeGraphString}`
      : "",
    currentReleasePercentageString: currentReleaseValues
      ? `${t("PageReleaseChart.currentDayRemainingReleaseValues.currentPrecentage")} ${getCurrentDayReleasePercentage()}`
      : "",
    title:
      displayReleaseGraphType == shareableItemTypes.agri_multicote_customize
        ? customizedSolution
          ? customizedSolution
          : ""
        : recommendedSolutionName
        ? recommendedSolutionName
        : "",

    unitValue: releaseGraphData?.applicationVolume?.length > 0 ? (releaseGraphData?.applicationVolume[valueSelected].value as number) : 0,
    unitType:
      releaseGraphData?.applicationVolume?.length > 0
        ? (releaseGraphData?.applicationVolume[valueSelected].mesurmentUnit as string)
        : (SMALL_MESURMENT_UNIT as any)[activeMetricMethod],
  };
  const sendToAgronomist = () => {
    const selected = selectedCrop ? selectedCrop : "1";
    const productCode = isAgri ? `${t("PageReleaseChart.sendToAgronomist.materialCode")} ${recommendedMaterialCode}` : "";
    const msg = `${t("PageReleaseChart.sendToAgronomist.preUserName")} ${user?.name} ${t("PageReleaseChart.sendToAgronomist.preCrop")} ${t(
      `crops.${selected}`
    )} ${t("PageReleaseChart.sendToAgronomist.preYield")} ${expectedYield} ${(BIG_MESURMENT_UNIT as any)[activeMetricMethod]}.\n${t(
      "PageReleaseChart.sendToAgronomist.preStartingDate"
    )} ${plantingDate}\n${t("PageReleaseChart.sendToAgronomist.preFinalDate")} ${finalHarvestDate}\n${t(
      "PageReleaseChart.sendToAgronomist.preSolutionName"
    )} ${dataTitle.title}\n${productCode}\n${t("PageReleaseChart.sendToAgronomist.preVolumeApplication")} ${
      releaseGraphData.applicationVolume[valueSelected].value
    } ${releaseGraphData.applicationVolume[valueSelected].mesurmentUnit}`;
    dispatch(GeneralState.Actions.setSummaryDescription(msg));
    history.push(LOGGED_IN_ROUTES.CONTACT_US);
  };

  //------------ Modals ---------------------//
  const closeSaveItemModalHandle = () => {
    setShowSaveItemModal(false);
  };
  const openSaveItemModalHandle = () => {
    setShowSaveItemModal(true);
  };
  //-----------------------------------------//

  const saveSolutionHandler = async () => {
    await dispatch(AgriSolutionState.Actions.setIsUserSave(true));
    await dispatch(AgriSolutionState.Thunks.saveProcess());

    if (isUserSave) {
      setShowBlackSnackbar(true);
    }
  };

  const closeSnackbarHandler = () => {
    setShowBlackSnackbar(false);
  };
  const [customizedGraph, setCustomizedGraph] = useState<ReleaseGraph[] | null>(releaseGraphData.customizedReleaseGraph);
  const [recommendedGraph, setRecommendedGraph] = useState<ReleaseGraph[] | null>(releaseGraphData.shelfSolutionReleaseGraph);

  useEffect(() => {
    console.log("recommendedGraph", recommendedGraph);
    console.log("customizedGraph", customizedGraph);
    setCustomizedGraph(releaseGraphData.customizedReleaseGraph);
    setRecommendedGraph(releaseGraphData.shelfSolutionReleaseGraph);
  }, [releaseGraphData]);

  const graphObj =
    displayReleaseGraphType == shareableItemTypes.agri_multicote_customize
      ? releaseGraphData.customizedReleaseGraph
      : releaseGraphData.shelfSolutionReleaseGraph;

  const firstMonth = graphObj && graphObj.length > 0 && graphObj[0].month;

  const graphData = graphObj?.map((month) => {
    return {
      name: getMonthFromNumber(((firstMonth as number) + month.index) % 12),
      N: month.nutrient[0].releaseValues[valueSelected].value,
      P: month.nutrient[1].releaseValues[valueSelected].value,
      K: month.nutrient[2].releaseValues[valueSelected].value,
      tooltip: {
        N: month.nutrient[0].releaseValues[valueSelected].remainValue,
        P: month.nutrient[1].releaseValues[valueSelected].remainValue,
        K: month.nutrient[2].releaseValues[valueSelected].remainValue,
      },
    };
  });
  console.count("rendering");
  console.log({ farmStateData });

  // TEMP: TO SHOW CARBON FOOTPRINT RESPONSE
  const [cfpResponse, setCfpResponse] = useState<CarbonFootprintResponse | null>(null);
  const [cfpLoading, setCfpLoading] = useState<boolean>(false);

  async function onCalculateCarbonFootprint() {
    if (!savedItemId) return;

    // Start the loading process
    setCfpResponse(null);
    setCfpLoading(true);
    const response = await Api.CFP.initiateCalculation(savedItemId);
    const crfCalculationId = (response.data as CarbonFootprintResponse).crfApi.calculationId ?? "";
    const tdCalculationId = (response.data as CarbonFootprintResponse).tdApi.calculationId ?? "";

    if (response.error) return;

    // Never exit the loop while the calculation
    // is still processing
    let calculationProcessing = true;
    let crfObject = {};
    let tdObject = {};

    do {
      const crfStatus = await Api.CFP.checkCalulcationStatus(crfCalculationId);
      const tdStatus = await Api.CFP.checkCalulcationStatus(tdCalculationId);

      console.count("PERFORMING A DATA CHECK");

      if (crfStatus.data.toLowerCase() === "stored" && tdStatus.data.toLowerCase() === "stored") {
        calculationProcessing = false;
        console.log("CALUCALTIONS DONE", { crfStatus: crfStatus.data, tdStatus: tdStatus.data });

        const crfData = await Api.CFP.collectCalculationData(crfCalculationId);
        const tdData = await Api.CFP.collectCalculationData(tdCalculationId);

        crfObject = JSON.parse(crfData.data);
        tdObject = JSON.parse(tdData.data);

        console.log({
          crfData: crfObject,
          tdData: tdObject,
        });
      }

      await delay(3000);
    } while (calculationProcessing);

    // Storing the data in the database
    await Api.CFP.storeCalculationData({
      crfCalculationId,
      crfData: JSON.stringify(crfObject),
      savedItemId,
      tdCalculationId,
      tdData: JSON.stringify(tdObject),
    });

    dispatch(AgriSolutionState.Actions.toggleGreenDescription());

    const crfRows = (crfObject as any).Result.Results[0].Tables[0].Rows;
    const tdRows = (tdObject as any).Result.Results[0].Tables[0].Rows;

    const nVolTrad = getTierZeroValueByName(tdRows, "Nitrogen volatilization");
    const nRunoffTrad = getTierZeroValueByName(tdRows, "Nitrogen runoff");
    const nLeachingTrad = getTierZeroValueByName(tdRows, "Nitrogen leaching");
    const nTerrestrialTrad = getTierZeroValueByName(tdRows, "Eutrophication, terrestrial");
    const nMarineTrad = getTierZeroValueByName(tdRows, "Eutrophication, marine");
    const cfpTrad = getTierZeroValueByName(tdRows, "Climate change");

    const nVolMult = getTierZeroValueByName(crfRows, "Nitrogen volatilization");
    const nRunoffMult = getTierZeroValueByName(crfRows, "Nitrogen runoff");
    const nLeachingMult = getTierZeroValueByName(crfRows, "Nitrogen leaching");
    const nTerrestrialMult = getTierZeroValueByName(crfRows, "Eutrophication, terrestrial");
    const nMarineMult = getTierZeroValueByName(crfRows, "Eutrophication, marine");
    const cfpMult = getTierZeroValueByName(crfRows, "Climate change");

    dispatch(
      CFPTableActions.setEntireValues({
        carbonFootprint: {
          multicoteTechnology: cfpMult,
          traditional: cfpTrad,
          fertigationRating: calculateFertigationRate(cfpMult, cfpTrad),
        },
        nitrogenLeaching: {
          multicoteTechnology: nLeachingMult,
          traditional: nLeachingTrad,
          fertigationRating: calculateFertigationRate(nLeachingMult, nLeachingTrad),
        },
        nitrogenRunoff: {
          multicoteTechnology: nRunoffMult,
          traditional: nRunoffTrad,
          fertigationRating: calculateFertigationRate(nRunoffMult, nRunoffTrad),
        },
        nitrogenVolatilization: {
          multicoteTechnology: nVolMult,
          traditional: nVolTrad,
          fertigationRating: calculateFertigationRate(nVolMult, nVolTrad),
        },
        nitrogenTerrestrialEutrophication: {
          multicoteTechnology: nTerrestrialMult,
          traditional: nTerrestrialTrad,
          fertigationRating: calculateFertigationRate(nTerrestrialMult, nTerrestrialTrad),
        },
        // nitrogenMarinetrialEutrophication: {
        //     multicoteTechnology: nMarineMult,
        //     traditional: nMarineTrad,
        //     fertigationRating: calculateFertigationRate(nMarineMult, nMarineTrad),
        // },
      })
    );

    console.log("CALCS", {
      carbonFootprint: {
        multicoteTechnology: cfpMult,
        traditional: cfpTrad,
        fertigationRating: calculateFertigationRate(cfpMult, cfpTrad),
      },
      nitrogenLeaching: {
        multicoteTechnology: nLeachingMult,
        traditional: nLeachingTrad,
        fertigationRating: calculateFertigationRate(nLeachingMult, nLeachingTrad),
      },
      nitrogenRunoff: {
        multicoteTechnology: nRunoffMult,
        traditional: nRunoffTrad,
        fertigationRating: calculateFertigationRate(nRunoffMult, nRunoffTrad),
      },
      nitrogenVolatilization: {
        multicoteTechnology: nVolMult,
        traditional: nVolTrad,
        fertigationRating: calculateFertigationRate(nVolMult, nVolTrad),
      },
      nitrogenTerrestrialEutrophication: {
        multicoteTechnology: nTerrestrialMult,
        traditional: nTerrestrialTrad,
        fertigationRating: calculateFertigationRate(nTerrestrialMult, nTerrestrialTrad),
      },
      nitrogenMarinetrialEutrophication: {
        multicoteTechnology: nMarineMult,
        traditional: nMarineTrad,
        fertigationRating: calculateFertigationRate(nMarineMult, nMarineTrad),
      },
    });

    // Formatting the response to show it in the UI
    // this is temporary and to be removed
    const formattedResponse: CarbonFootprintResponse = {
      api1: (response.data as CarbonFootprintResponse).api1,
      api2: (response.data as CarbonFootprintResponse).api2,
      realApi1: (response.data as CarbonFootprintResponse).realApi1,
      realApi2: (response.data as CarbonFootprintResponse).realApi2,
      crfApi: crfObject,
      tdApi: tdObject,
    };

    // End the loading process
    setCfpResponse(formattedResponse);
    setCfpLoading(false);
  }

  const isThereFarmerPractice = () => {
    console.log(farmStateData.farmerPractice);

    return (
      farmStateData &&
      farmStateData.farmerPractice &&
      farmStateData.farmerPractice.n &&
      farmStateData.farmerPractice.p &&
      farmStateData.farmerPractice.k &&
      !Number.isNaN(farmStateData.farmerPractice.n) &&
      !Number.isNaN(farmStateData.farmerPractice.p) &&
      !Number.isNaN(farmStateData.farmerPractice.k)
    );
  };

  return (
    <>
      {ShowBlackSnackbar && (
        <div className={classes.snackbar_container}>
          <FmBlackPopup text={t("saveItemPopup.snackbar")} snackbar={true} onClose={closeSnackbarHandler} />
        </div>
      )}

      <ShowWhen when={cfpLoading}>
        <FmPageLoader />
      </ShowWhen>

      <div className={classes.container}>
        <div className={isMobile ? classes.mainPage_mobile : classes.mainPage}>
          <div className={isMobile ? classes.mobile_topPage : classes.topPage}>
            <FmProductTitle
              title={dataTitle.title}
              nutrients={
                farmStateData && farmStateData.cropDemand && farmStateData.cropDemand[valueSelected]
                  ? farmStateData.cropDemand[valueSelected].cropDemand
                  : null
              }
              farmerPracrice={isThereFarmerPractice() ? farmStateData.farmerPractice : undefined}
              unitValue={dataTitle.unitValue}
              unitType={dataTitle.unitType}
            />
            <div className={isMobile ? classes.mobile_links : classes.links}>
              <ShowWhen when={!pdfLodaing}>
                {!isMobile && <FmUnderlineNav title={t("PageReleaseChart.buttons.downloadFile")} onClick={generatePdf} />}
              </ShowWhen>

              <ShowWhen when={pdfLodaing}>{!isMobile && <CircularProgress size={24} />}</ShowWhen>

              <FmUnderlineNav
                title={isMobile ? t("PageReleaseChart.buttons.shareLink.mobile") : t("PageReleaseChart.buttons.shareLink.desktop")}
                onClick={handleClickOnShare}
              />

              <FmUnderlineNav
                title={t("saveItemPopup.save")}
                onClick={!isUserSave ? openSaveItemModalHandle : saveSolutionHandler}
                disabled={false}
              />
              <FmUnderlineNav title={t("PageReleaseChart.buttons.sendToPro")} onClick={sendToAgronomist} />
            </div>
          </div>

          <ShowWhen when={Config.isDebug}>
            <ShowWhen when={userSolutionNPK && userSolutionNPK.n != undefined && userSolutionNPK.p != undefined && userSolutionNPK.k != undefined}>
              <Stack direction={"row"} gap={4}>
                <p>User Solution N: '{userSolutionNPK.n}'</p>
                <p>User Solution P: '{userSolutionNPK.p}'</p>
                <p>User Solution K: '{userSolutionNPK.k}'</p>
              </Stack>
            </ShowWhen>
          </ShowWhen>

          <div className={classes.graph_container}>
            <div className={isMobile ? classes.mobile_radioButton_container : classes.radioButton_container}>
              <FmRadioButton
                gap={17}
                onChange={(e: any) => handleGraphType(e)}
                values={radioButtonArray}
                title={t("PageReleaseChart.greenMessage.radioButtonLabel")}
                noMarginStart={false}
              />
              <div
                style={
                  isMobile
                    ? { width: "70%" }
                    : {
                        marginLeft: "6em",
                        width: "37%",
                        flexDirection: "column",
                      }
                }
              >
                <ShowWhen when={!isThereFarmerPractice()}>
                  <div className={classes.currentReleaseString}>{dataTitle.currentReleaseString}</div>
                  <div className={classes.currentReleaseString}>{dataTitle.currentReleasePercentageString}</div>
                </ShowWhen>

                <ShowWhen when={isThereFarmerPractice()}>
                  <div className={classes.currentReleaseString}>{dataTitle.farmerPraceiceNPKTitle}</div>
                  <div className={classes.currentReleaseString}>{dataTitle.farmerPracticeCurrentReleasePercentageString}</div>
                </ShowWhen>
              </div>
            </div>
            <span className={classes.units_span}>{(SMALL_MESURMENT_UNIT as any)[activeMetricMethod]}</span>
            <div ref={releaseChartRef}>
              <FmReleaseChart graphData={graphData} graphType={graphType} id={"divToPrint"} />
            </div>
          </div>

          <ShowWhen when={!cfpResponse}>
            <div className={classes.carbonTools}>
              <img style={{ position: "absolute", top: -27, left: -46 }} src={theme.images.tiltedCo2Logo} alt="co2" />

              <Typography sx={{ fontWeight: "500", fontSize: 20 }}>{t("pageStep3.carbonFootprintDesc")}</Typography>

              <FmButton
                disabled={cfpLoading}
                loading={cfpLoading}
                onClick={onCalculateCarbonFootprint}
                fullWidth="mobileFullWidth"
                icon={theme.images.whitePlus}
              >
                {t("pageStep3.carbonFootprintButtonText")}
              </FmButton>
            </div>
          </ShowWhen>

          <ShowWhen when={cfpResponse}>
            <Box sx={{ width: "100%", marginBottom: "1.5rem" }}>
              <FmCarbonFootprintTable />
            </Box>
          </ShowWhen>

          {isGreenDescOpen ? (
            <div className={isMobile ? classes.mobile_greenDescription_container : classes.greenDescription_container}>
              <FmGreenDescription />
            </div>
          ) : (
            <div className={isMobile ? classes.mobile_greenDescription_container_open : classes.greenDescription_container_open}>
              <FmGreenDescription />
            </div>
          )}

          <div className={isMobile ? classes.mobile_warning_container : classes.warning_container}>
            <FmWarningBox icon="/multi-match/assets/envelope.png" description={warningDescription} />

            <ShowWhen when={Config.isDebug && !cfpLoading && cfpResponse}>
              <Stack marginTop={4} marginBottom={8} direction="column" gap={2}>
                <Accordion sx={{ backgroundColor: "#F3F3F3" }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>Detailed API Parameters</AccordionSummary>

                  <AccordionDetails>
                    <h4>Api 1</h4>
                    <pre>
                      {JSON.stringify(cfpResponse?.api1, null, 4)}
                      <hr />
                      <h4>Api 2</h4>
                      {JSON.stringify(cfpResponse?.api2, null, 4)}
                    </pre>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ backgroundColor: "#F3F3F3" }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>Real API Parameters</AccordionSummary>

                  <AccordionDetails>
                    <pre>
                      <h4>Api 1</h4>

                      {JSON.stringify(cfpResponse?.realApi1, null, 4)}
                      <hr />
                      <h4>Api 2</h4>
                      {JSON.stringify(cfpResponse?.realApi2, null, 4)}
                    </pre>
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ backgroundColor: "#F3F3F3" }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>Real API Response</AccordionSummary>

                  <AccordionDetails>
                    <pre>
                      <h4>CRF Api</h4>

                      {JSON.stringify({ ...cfpResponse?.crfApi, Result: "long_json_string" }, null, 4)}
                      <hr />
                      <h4>TD Api</h4>
                      {JSON.stringify({ ...cfpResponse?.tdApi, Result: "long_json_string" }, null, 4)}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </ShowWhen>
          </div>
        </div>
        <FmAllModals
          saveItemModalIsOpen={showSaveItemModal}
          saveItemModalOnClose={closeSaveItemModalHandle}
          saveItemModalAction={saveSolutionHandler}
        />
      </div>
    </>
  );
};

export default PageReleaseChart;
